/**
 * WeekNavigation - Week selector controls for the planner
 *
 * Features:
 * - Navigate to previous/next week
 * - Jump to today's week
 * - Display current week range
 */

import { useMemo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { formatWeekRange, getWeekStart } from "./types";

// ============================================================================
// Types
// ============================================================================

export interface WeekNavigationProps {
  /** Current week start date (Monday) */
  weekStart: Date;
  /** Called when week changes */
  onWeekChange: (newWeekStart: Date) => void;
  /** Additional class name */
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export function WeekNavigation({
  weekStart,
  onWeekChange,
  className,
}: WeekNavigationProps) {
  // Format the week range for display
  const weekRangeText = useMemo(() => formatWeekRange(weekStart), [weekStart]);

  // Check if current week is today's week
  const isCurrentWeek = useMemo(() => {
    const todayWeekStart = getWeekStart(new Date());
    return weekStart.getTime() === todayWeekStart.getTime();
  }, [weekStart]);

  // Navigate to previous week
  const handlePrevWeek = useCallback(() => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() - 7);
    onWeekChange(newDate);
  }, [weekStart, onWeekChange]);

  // Navigate to next week
  const handleNextWeek = useCallback(() => {
    const newDate = new Date(weekStart);
    newDate.setDate(newDate.getDate() + 7);
    onWeekChange(newDate);
  }, [weekStart, onWeekChange]);

  // Jump to today's week
  const handleToday = useCallback(() => {
    const todayWeekStart = getWeekStart(new Date());
    onWeekChange(todayWeekStart);
  }, [onWeekChange]);

  return (
    <div className={className}>
      {/* Mobile Layout */}
      <div className="flex sm:hidden flex-col gap-2">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="icon"
            onClick={handlePrevWeek}
            aria-label="Previous week"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>

          <span className="text-base font-semibold text-center">
            {weekRangeText}
          </span>

          <Button
            variant="outline"
            size="icon"
            onClick={handleNextWeek}
            aria-label="Next week"
          >
            <ChevronRight className="h-5 w-5" />
          </Button>
        </div>

        {!isCurrentWeek && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToday}
            className="w-full"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Jump to This Week
          </Button>
        )}
      </div>

      {/* Desktop Layout */}
      <div className="hidden sm:flex items-center gap-2">
        <Button
          variant="outline"
          size="icon"
          onClick={handlePrevWeek}
          aria-label="Previous week"
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>

        <div className="flex items-center gap-3 min-w-[200px] justify-center">
          <span className="text-lg font-semibold">
            Week of {weekRangeText}
          </span>
        </div>

        <Button
          variant="outline"
          size="icon"
          onClick={handleNextWeek}
          aria-label="Next week"
        >
          <ChevronRight className="h-5 w-5" />
        </Button>

        {!isCurrentWeek && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleToday}
            className="ml-2"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Today
          </Button>
        )}
      </div>
    </div>
  );
}
